/**
 * The management connection's card, with the internet WAN card's controls
 * (operator, 2026-10-10: "the mngt wan should have full controls like the
 * internet wan").
 *
 * What the card can set is what the management write takes in ONE request
 * (`POST /olts/:id/onus/:onuId/management-mode`, the header Mgmt dialog's
 * route): how the ONU is managed, which ACS, the management VLAN, and DHCP or
 * a static address. The firewall rides the internet card's own standalone
 * verb (`/firewall`, the router first, else the chassis), so the router has
 * one firewall that two cards show, never two writers that can disagree.
 *
 * Deliberately NOT here, though the internet card has them: PPPoE and IPv6
 * (the management write has neither) and Delete (this is the platform's own
 * channel to the router; deleting it takes the router off the platform).
 */

export type ManagementMode = 'tr069' | 'omci';
export type ManagementAddressing = 'dhcp' | 'static';

export interface ManagementCardChoice {
  mode: ManagementMode;
  /** The ACS profile, TR-069 only; '' when the OLT offers none. */
  profileId: string;
  /** The management VLAN, as the picker holds it; '' = none chosen. */
  vlan: string;
  addressing: ManagementAddressing;
  staticIp: string;
  staticMask: string;
  staticGateway: string;
  firewall: boolean;
}

/** What stops Apply, in the card's words, or null. */
export function managementApplyBlocked(
  choice: ManagementCardChoice,
  opts: {
    /** The chassis generation's refusal of a static management address (a C650), or null. */
    staticRefusal: string | null;
    /** The OLT offers at least one ACS profile. */
    profilesOffered: boolean;
  },
): string | null {
  if (choice.mode === 'tr069' && !choice.vlan) {
    return 'Choose the management VLAN: without one the router never reaches the ACS.';
  }
  if (choice.mode === 'tr069' && opts.profilesOffered && !choice.profileId) return 'Choose the ACS.';
  if (choice.addressing === 'static') {
    if (opts.staticRefusal) return opts.staticRefusal;
    if (choice.staticIp.trim().length < 7 || choice.staticMask.trim().length < 7) {
      return 'A static management address needs its address and mask.';
    }
  }
  return null;
}

/** The management write's body — the Mgmt dialog's, applied for real. */
export function managementApplyBody(choice: ManagementCardChoice): Record<string, unknown> {
  const vlan = /^\d+$/.test(choice.vlan) ? Number(choice.vlan) : null;
  return {
    mode: choice.mode,
    ...(choice.mode === 'tr069' && choice.profileId ? { tr069ProfileId: choice.profileId } : {}),
    ...(vlan !== null ? { managementVlan: vlan, vlanPriority: 0 } : {}),
    addressing: choice.addressing,
    ...(choice.addressing === 'static'
      ? {
          staticIp: choice.staticIp.trim(),
          staticMask: choice.staticMask.trim(),
          ...(choice.staticGateway.trim() ? { staticGateway: choice.staticGateway.trim() } : {}),
        }
      : {}),
    dryRun: false,
  };
}

function describe(choice: ManagementCardChoice): string {
  const how = choice.mode === 'tr069' ? 'TR-069' : 'OMCI';
  const where = choice.vlan ? ` on VLAN ${choice.vlan}` : '';
  const address = choice.addressing === 'static' ? `static ${choice.staticIp.trim()}` : 'DHCP';
  return `${how}${where}, ${address}`;
}

/** The confirm sentence: what is applied, and what a wrong setting costs. */
export function managementApplyConfirm(
  choice: ManagementCardChoice,
  opened: { mode: ManagementMode },
  firewallMoved: boolean,
): string {
  const head = `Apply management as ${describe(choice)}${firewallMoved ? `, firewall ${choice.firewall ? 'on' : 'off'}` : ''}?`;
  const leaving =
    choice.mode === 'omci' && opened.mode === 'tr069'
      ? '\n\nThe router stops being managed over TR-069: its WAN, WiFi and LAN are no longer set from this page.'
      : '';
  return `${head}\n\nIt is set on the OLT and the router reconnects to the ACS. A wrong setting takes the router off the platform until it is put right from here.${leaving}`;
}

/** The tray's done text. */
export function managementDoneText(
  serial: string,
  choice: ManagementCardChoice,
  outcome: { warnings: number; firewallNote: string | null },
): string {
  const head = `${serial}: management applied as ${describe(choice)}`;
  const warned = outcome.warnings > 0 ? `, with warnings — open ${serial} to read them` : '';
  return `${head}${warned}.${outcome.firewallNote ? ` ${outcome.firewallNote}` : ''}`;
}
